import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { captureError } from '../../services/sentry';
import { isDynamicImportError, recoverFromStaleChunk } from '../../utils/chunkReload';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  /**
   * The error is a stale code-split chunk after a deploy — NOT a real crash.
   * We self-heal with a "Live update in progress" reload instead of showing
   * the error page.
   */
  staleChunk: boolean;
  /** Auto-reload was suppressed by the loop guard — offer a manual update. */
  recoverySuppressed: boolean;
}

/**
 * Error Boundary component that catches JavaScript errors in child components.
 * Displays a user-friendly error message and provides recovery options.
 *
 * Usage:
 * <ErrorBoundary>
 *   <SomeComponent />
 * </ErrorBoundary>
 *
 * Or with custom fallback:
 * <ErrorBoundary fallback={<CustomErrorUI />}>
 *   <SomeComponent />
 * </ErrorBoundary>
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      staleChunk: false,
      recoverySuppressed: false
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error, staleChunk: isDynamicImportError(error) };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Stale chunk after a deploy — self-heal via "Live update" reload instead
    // of the error page. Not a real crash: skip Sentry (pure deploy noise).
    if (isDynamicImportError(error)) {
      console.info('Stale chunk detected after deploy — live-updating:', error.message);
      const reloading = recoverFromStaleChunk();
      if (!reloading) {
        // Loop guard suppressed the auto-reload — offer a manual update.
        this.setState({ recoverySuppressed: true });
      }
      return;
    }

    // Log to console for debugging
    console.error('ErrorBoundary caught an error:', error);
    console.error('Component stack:', errorInfo.componentStack);

    this.setState({ errorInfo });

    // Report to Sentry
    captureError(error, { component_stack: errorInfo.componentStack || 'unknown' });

    // Call optional error callback
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  handleReload = (): void => {
    window.location.reload();
  };

  handleGoHome = (): void => {
    window.location.href = '/';
  };

  handleRetry = (): void => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      // Stale deploy — show the live-update experience, never the error page.
      if (this.state.staleChunk) {
        return (
          <div className="min-h-[400px] flex items-center justify-center p-8">
            <div className="max-w-md w-full glass-card p-8 text-center">
              {this.state.recoverySuppressed ? (
                <>
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-6">
                    <RefreshCw className="w-8 h-8 text-primary" />
                  </div>
                  <h2 className="text-xl font-semibold text-neutral-800 mb-2">
                    A new version is available
                  </h2>
                  <p className="text-neutral-600 mb-6">
                    PropCloudAI has been updated. Refresh to load the latest version.
                  </p>
                  <button
                    onClick={this.handleReload}
                    className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
                  >
                    <RefreshCw className="w-4 h-4" />
                    Update now
                  </button>
                </>
              ) : (
                <>
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-6">
                    <div className="w-8 h-8 rounded-full border-4 border-neutral-200 border-t-primary animate-spin" />
                  </div>
                  <h2 className="text-xl font-semibold text-neutral-800 mb-2">
                    Live update in progress
                  </h2>
                  <p className="text-neutral-600">
                    Getting the latest version… this will only take a moment.
                  </p>
                </>
              )}
            </div>
          </div>
        );
      }

      // Custom fallback provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <div className="min-h-[400px] flex items-center justify-center p-8">
          <div className="max-w-md w-full glass-card p-8 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-100 mb-6">
              <AlertTriangle className="w-8 h-8 text-red-600" />
            </div>

            <h2 className="text-xl font-semibold text-neutral-800 mb-2">
              Something went wrong
            </h2>

            <p className="text-neutral-600 mb-6">
              An unexpected error occurred. This has been logged and we'll look into it.
            </p>

            {/* Show error message in development */}
            {import.meta.env.DEV && this.state.error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 text-left">
                <p className="text-sm font-mono text-red-800 break-all">
                  {this.state.error instanceof Error ? this.state.error.message : String(this.state.error)}
                </p>
                {this.state.errorInfo && (
                  <details className="mt-2">
                    <summary className="text-xs text-red-600 cursor-pointer">
                      Component Stack
                    </summary>
                    <pre className="text-xs text-red-700 mt-2 overflow-auto max-h-32">
                      {this.state.errorInfo.componentStack}
                    </pre>
                  </details>
                )}
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={this.handleRetry}
                className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                Try Again
              </button>

              <button
                onClick={this.handleGoHome}
                className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-neutral-100 text-neutral-700 rounded-lg hover:bg-neutral-200 transition-colors"
              >
                <Home className="w-4 h-4" />
                Go Home
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Wrapper for page-level error boundaries
 */
export const PageErrorBoundary: React.FC<{ children: ReactNode }> = ({ children }) => {
  return (
    <ErrorBoundary
      onError={(error) => {
        captureError(error, { boundary: 'page' });
      }}
    >
      {children}
    </ErrorBoundary>
  );
};

export default ErrorBoundary;
